import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { DealRole } from '__generated__/globalTypes';
import { DataDisplayTable, LinkButton, SpinnerComponent, type WithSubData } from 'ui-kit';
import { useServicedDeals } from './getServicedDeals_gql';
import { isValidDealId } from './utils';

import { GetServicedDeals } from './__generated__/getServicedDeals_gql';

type GetServicedDeals_getServicedDeals = NonNullable<
  NonNullable<GetServicedDeals>['getServicedDeals']
>[number];
type GetServicedDeals_getServicedDeals_counterparty = NonNullable<
  NonNullable<GetServicedDeals>['getServicedDeals'][number]
>['counterparty'];
const REMITTANCE_DEAL_PATH = '/portfolio/remittance/dealId';

function getCounterpartyDisplayName(
  counterparty: GetServicedDeals_getServicedDeals_counterparty | null | undefined,
): string {
  if (!counterparty) return '—';
  if ('name' in counterparty && counterparty.name) return counterparty.name;
  return '—';
}

type ServicedDealRow = GetServicedDeals_getServicedDeals;

/** DataDisplayTable expects rows to include __typename and last_updated_date; we add them so the table gets the correct row type. */
function toTableRows(deals: ServicedDealRow[]): WithSubData<ServicedDealRow>[] {
  return deals.map((d) => ({ ...d, __typename: 'Deal', last_updated_date: '' }));
}

function useServicedDealsColumns(onView: (dealId: string) => void) {
  return useMemo<ColumnDef<WithSubData<ServicedDealRow>, unknown>[]>(
    () => [
      {
        id: 'deal',
        accessorFn: (row) => row.listing?.name ?? row.id,
        header: 'Deal',
        cell: (ctx) => <span>{ctx.getValue() as string}</span>,
        meta: { alignment: 'left', numeric: false },
        enableSorting: true,
      },
      {
        id: 'counterparty',
        accessorFn: (row) => getCounterpartyDisplayName(row.counterparty),
        header: 'Counterparty',
        cell: (ctx) => <span>{ctx.getValue() as string}</span>,
        meta: { alignment: 'left', numeric: false },
        enableSorting: true,
      },
      {
        id: 'action',
        header: 'Action',
        cell: (ctx) => {
          const id = ctx.row.original.id;
          const valid = isValidDealId(id);
          return (
            <LinkButton
              text="View latest month"
              onClick={() => valid && onView(id)}
              disabled={!valid}
            />
          );
        },
        meta: { alignment: 'left', numeric: false },
        enableSorting: false,
      },
    ],
    [onView],
  );
}

function DealSectionTable({
  title,
  deals,
  onView,
}: {
  title: string;
  deals: ServicedDealRow[];
  onView: (dealId: string) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useServicedDealsColumns(onView);

  if (deals.length === 0) return null;

  const tableData = toTableRows(deals);

  return (
    <section className="mb-8">
      <h3 className="mb-2 text-sm font-medium text-slate-300">{title}</h3>
      <DataDisplayTable
        data={tableData}
        columns={columns}
        noDataMessage="No deals in this section"
        sorting={{ state: sorting, onSortingChanged: setSorting }}
        getRowId={(row) => row.id ?? ''}
        isDraggable={false}
        canResize={false}
      />
    </section>
  );
}

export const ServicedDealsTable = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useServicedDeals();

  const { servicingDeals, purchasedDeals } = useMemo(() => {
    const list = data?.getServicedDeals ?? [];
    const servicing = list.filter((d) => d.role === DealRole.SELLER);
    const purchased = list.filter((d) => d.role === DealRole.BUYER);
    return { servicingDeals: servicing, purchasedDeals: purchased };
  }, [data?.getServicedDeals]);

  const handleView = (dealId: string) => {
    navigate(`${REMITTANCE_DEAL_PATH}/${encodeURIComponent(dealId)}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerComponent />
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-red-400">Failed to load serviced deals. {error.message}</p>;
  }

  const hasDeals = servicingDeals.length > 0 || purchasedDeals.length > 0;
  if (!hasDeals) {
    return (
      <p className="py-6 text-slate-400">
        No serviced deals are currently available. Completed deals in which you are the seller (with
        servicing retained) or the buyer will appear here.
      </p>
    );
  }

  return (
    <div className="text-slate-200">
      <DealSectionTable title="Deals I'm servicing" deals={servicingDeals} onView={handleView} />
      <DealSectionTable title="Deals I bought" deals={purchasedDeals} onView={handleView} />
    </div>
  );
};
